var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { normalize } from './normalize';
import { parseTsl } from './parser';
import { normalizeLabel } from './registry';
import { relationBehavior } from './relations';
import { quoteIfNeeded, tokenize } from './tokenizer';
var MAX_SYNC_SUGGESTIONS = 12;
/** Applies a suggestion to the text, returning the new text and caret position. */
export var applySuggestion = function (text, replaceRange, insertText, caretOffset) {
    var before = text.slice(0, replaceRange.start);
    var after = text.slice(replaceRange.end);
    // Self-positioning suggestions (e.g. an operator seeding empty quotes) place
    // the caret themselves and are inserted verbatim, without an auto space.
    if (caretOffset !== undefined) {
        return {
            text: "".concat(before).concat(insertText).concat(after),
            caret: before.length + caretOffset,
        };
    }
    var needsTrailingSpace = !after.startsWith(' ');
    var inserted = "".concat(insertText).concat(needsTrailingSpace ? ' ' : '');
    return {
        text: "".concat(before).concat(inserted).concat(after),
        caret: before.length + inserted.length,
    };
};
var labelMatchesPrefix = function (label, prefix) {
    return prefix.length === 0 || normalizeLabel(label).startsWith(normalizeLabel(prefix));
};
var fieldSuggestion = function (field) { return ({
    kind: 'field',
    label: field.label,
    insertText: field.label,
    detail: field.dataType,
}); };
var pseudoFieldSuggestion = function (pseudo) { return ({
    kind: 'pseudoField',
    label: pseudo.label,
    insertText: pseudo.label,
}); };
var fieldStartSuggestions = function (registry, prefix) { return __spreadArray(__spreadArray([], registry.fields
    .filter(function (field) { return labelMatchesPrefix(field.label, prefix); })
    .map(fieldSuggestion), true), registry.pseudoFields
    .filter(function (pseudo) { return labelMatchesPrefix(pseudo.label, prefix); })
    .map(pseudoFieldSuggestion), true); };
/** Empty quote pair seeded after a string operator for the value. */
var EMPTY_QUOTES = '""';
var operatorSuggestions = function (field, prefix) {
    return field.relations
        .filter(function (relation) { return labelMatchesPrefix(relation.label, prefix); })
        .map(function (relation) {
        // String operators taking one value seed empty quotes so a multi-word
        // value ("Karel Čapek") can be typed straight away — quotes are what
        // keeps it tokenized as a single value. The caret lands between them.
        var seedsQuotedValue = relation.behavior.arity === 1 &&
            (field.dataType === 'STRING' || field.dataType === 'TEXT');
        var insertText = seedsQuotedValue
            ? "".concat(relation.label, " ").concat(EMPTY_QUOTES)
            : relation.label;
        return __assign({ kind: 'operator', label: relation.label, insertText: insertText, detail: relation.behavior.symbols[0] }, (seedsQuotedValue ? { caretOffset: relation.label.length + 2 } : {}));
    });
};
var pseudoValueSuggestions = function (pseudo, prefix) {
    return pseudo.values
        .filter(function (value) { return labelMatchesPrefix(value.label, prefix); })
        .map(function (value) { return ({
        kind: 'pseudoValue',
        label: value.label,
        insertText: quoteIfNeeded(value.label),
    }); });
};
var booleanValueSuggestions = function (registry, prefix) {
    return ['ano', 'ne']
        .filter(function (word) {
        return registry.parseBooleanWord(word) !== undefined &&
            labelMatchesPrefix(word, prefix);
    })
        .map(function (word) { return ({ kind: 'value', label: word, insertText: word }); });
};
/** The partially-typed token at the caret, if any. */
var partialTokenAt = function (tokens, caret) {
    return tokens.find(function (token) {
        return token.type !== 'symbol' && token.span.start < caret && caret <= token.span.end;
    });
};
/**
 * Extends the completion prefix backwards over a trailing fulltext run so
 * multi-word field labels complete naturally (`Přírůstkové čí|` offers
 * `Přírůstkové číslo` even though `Přírůstkové` alone matched nothing).
 */
var extendedFieldPrefix = function (text, parseEnd, prefix, registry) {
    var parsed = parseTsl(text.slice(0, parseEnd), registry);
    var lastGroup = parsed.groups[parsed.groups.length - 1];
    var lastClause = lastGroup === null || lastGroup === void 0 ? void 0 : lastGroup[lastGroup.length - 1];
    if (!lastClause || lastClause.kind !== 'fulltext') {
        return undefined;
    }
    // Only extend over a fulltext run that touches the parse boundary.
    if (text.slice(lastClause.span.end, parseEnd).trim() !== '') {
        return undefined;
    }
    var combined = "".concat(lastClause.text, " ").concat(prefix).trim();
    var hasMatch = registry.fields.some(function (field) { return labelMatchesPrefix(field.label, combined); }) ||
        registry.pseudoFields.some(function (pseudo) { return labelMatchesPrefix(pseudo.label, combined); });
    return hasMatch ? { prefix: combined, start: lastClause.span.start } : undefined;
};
/**
 * Computes completion suggestions at the caret.
 *
 * Field, operator, connector and pseudo-value suggestions are returned
 * synchronously. When the caret sits at a value position of a field whose
 * values live on the server, `valueContext` tells the host what to fetch
 * (paged — the host passes offset/limit); returned values are appended by
 * the host as `kind: 'value'` suggestions using {@link valueSuggestion}.
 */
export var suggest = function (text, caret, registry) {
    var tokens = tokenize(text);
    var partial = partialTokenAt(tokens, caret);
    var parseEnd = partial ? partial.span.start : caret;
    var rawPrefix = partial ? text.slice(partial.span.start, caret) : '';
    var prefix = rawPrefix.replace(/^"/, '');
    var replaceRange = partial
        ? { start: partial.span.start, end: partial.span.end }
        : { start: caret, end: caret };
    var parsed = parseTsl(text.slice(0, parseEnd), registry);
    var expectation = parsed.expectation;
    switch (expectation.type) {
        case 'operatorOrValue':
        case 'value':
        case 'value2': {
            var field = expectation.field;
            var items = [];
            if (expectation.type === 'operatorOrValue') {
                items.push.apply(items, operatorSuggestions(field, prefix));
            }
            if (field.dataType === 'BOOLEAN') {
                items.push.apply(items, booleanValueSuggestions(registry, prefix));
                return { replaceRange: replaceRange, items: items.slice(0, MAX_SYNC_SUGGESTIONS) };
            }
            // Values for BETWEEN bounds and dates are free-typed; suggest stored
            // values only for single-value positions of string-like fields.
            var relation = expectation.type === 'operatorOrValue'
                ? field.defaultRelation
                : expectation.relation;
            var suggestValues = expectation.type !== 'value2' &&
                relationBehavior(relation).arity === 1 &&
                (field.dataType === 'STRING' || field.dataType === 'TEXT');
            return __assign({ replaceRange: replaceRange, items: items.slice(0, MAX_SYNC_SUGGESTIONS) }, (suggestValues ? { valueContext: { fieldId: field.id, prefix: prefix } } : {}));
        }
        case 'pseudoValue':
            return {
                replaceRange: replaceRange,
                items: pseudoValueSuggestions(expectation.pseudo, prefix).slice(0, MAX_SYNC_SUGGESTIONS),
            };
        // `clauseStart` is the last expectation; `default` keeps the switch total
        // (consistent-return, default-case).
        default: {
            var extended = extendedFieldPrefix(text, parseEnd, prefix, registry);
            if (extended) {
                return {
                    replaceRange: { start: extended.start, end: replaceRange.end },
                    items: fieldStartSuggestions(registry, extended.prefix).slice(0, MAX_SYNC_SUGGESTIONS),
                };
            }
            var items = fieldStartSuggestions(registry, prefix);
            var connectors = parsed.clauses.length > 0 && prefix.length === 0
                ? [
                    {
                        kind: 'connector',
                        label: 'OR',
                        insertText: 'OR',
                    },
                ]
                : [];
            return {
                replaceRange: replaceRange,
                items: __spreadArray(__spreadArray([], items, true), connectors, true).slice(0, MAX_SYNC_SUGGESTIONS),
            };
        }
    }
};
/**
 * CZNA-217: {@link quoteIfNeeded}, plus quotes for a value that is a connector word —
 * a stored value `a` inserted bare would read back as AND. Without a registry the Trex
 * behaviour is unchanged.
 */
export var quoteValue = function (label, registry) {
    var quoted = quoteIfNeeded(label);
    if (quoted !== label || !registry) {
        return quoted;
    }
    var tokens = tokenize(label);
    return tokens.length === 1 && registry.matchConnector(tokens[0]) ? "\"".concat(label, "\"") : label;
};
/** Builds a value suggestion from a server-provided value label. */
export var valueSuggestion = function (label, detail, registry) { return ({
    kind: 'value',
    label: label,
    insertText: quoteValue(label, registry),
    detail: detail,
}); };
/** Case/diacritics-insensitive check used to highlight the matched prefix. */
export var suggestionMatches = function (suggestion, prefix) {
    return normalize(suggestion.label).startsWith(normalize(prefix));
};
